import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { WebStudioBillingOverview, WebStudioBootSnapshot, WebStudioScope } from "../api/boot";
import type { WebStudioFactsPlaneSnapshot } from "../api/generatedClient";
import { WebStudioCloudApiClient } from "../api/generatedClient";
import { createWebStudioFetchTransport } from "../api/transport";
import { billingOverviewQueryOptions, bootReadQueryOptions } from "./bootQuery";
import { useBootRefresh } from "./refresh";

export type BootReadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      boot: WebStudioBootSnapshot;
      factsPlane: WebStudioFactsPlaneSnapshot;
      protectedStateCleared: boolean;
    }
  | { kind: "failed"; message: string };

function useWebStudioClient(): WebStudioCloudApiClient {
  return useMemo(() => new WebStudioCloudApiClient(createWebStudioFetchTransport()), []);
}

/**
 * Server state lives exclusively in TanStack Query over the replaceable
 * generated-client adapters. Concurrent mounts of the same scope share one
 * request; a refresh bumps the client-side generation in the cache key.
 */
export function useBootRead(scope: WebStudioScope): BootReadState {
  const { token } = useBootRefresh();
  const client = useWebStudioClient();
  const query = useQuery(bootReadQueryOptions(client, scope, token));

  if (query.data) {
    return {
      kind: "ready",
      boot: query.data.boot,
      factsPlane: query.data.factsPlane,
      protectedStateCleared: query.data.protectedStateCleared,
    };
  }
  if (query.isError) {
    return {
      kind: "failed",
      message: query.error instanceof Error ? query.error.message : String(query.error),
    };
  }
  return { kind: "loading" };
}

/** Additive billing context: a slow or failed overview never blocks a page. */
export function useBillingOverview(scope: WebStudioScope): WebStudioBillingOverview | null {
  const { token } = useBootRefresh();
  const client = useWebStudioClient();
  const query = useQuery(billingOverviewQueryOptions(client, scope, token));
  return query.data ?? null;
}
